/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(() => {
  if (window.__swaggeroCardForms) return;
  window.__swaggeroCardForms = true;

  const openCart = () => {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { openMiniCart: true }
    }));
  };

  const bindForm = (form) => {
    if (!form || form.dataset.swaggeroBound === 'true') return;
    form.dataset.swaggeroBound = 'true';

    const select = form.querySelector('[data-swaggero-card-variant]');
    const button = form.querySelector('[data-swaggero-card-add]');
    const label = form.querySelector('[data-swaggero-card-add-text]');
    const sync = () => {
      const option = select?.options[select.selectedIndex];
      if (button) button.disabled = option?.dataset.available !== 'true';
    };

    select?.addEventListener('change', sync);
    sync();

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!button || button.disabled) return;

      const original = label?.textContent || 'ADD TO CART';
      button.disabled = true;
      button.classList.add('is-loading');
      if (label) label.textContent = 'ADDING...';

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        });
        if (!response.ok) throw new Error('Unable to add product');

        const item = await response.json();
        document.dispatchEvent(new CustomEvent('variant:added', { bubbles: true, detail: { variant: item } }));
        openCart();
        if (label) label.textContent = 'ADDED';
      } catch (error) {
        if (label) label.textContent = 'TRY AGAIN';
      } finally {
        window.setTimeout(() => {
          button.classList.remove('is-loading');
          sync();
          if (label) label.textContent = original;
        }, 1200);
      }
    });
  };

  const mount = (root = document) => root.querySelectorAll?.('[data-swaggero-card-form]').forEach(bindForm);
  mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1) mount(node);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

(() => {
  if (window.__swaggeroMobileCartFallback) return;
  window.__swaggeroMobileCartFallback = true;

  const isMobile = () => window.matchMedia('(max-width: 999px)').matches;
  const ensureCartOpen = () => {
    if (!isMobile()) return;
    const drawer = document.getElementById('mini-cart');
    if (!drawer || drawer.hasAttribute('open')) return;
    drawer.open = true;
  };

  document.addEventListener('cart:refresh', (event) => {
    if (!event.detail?.openMiniCart || !isMobile()) return;
    requestAnimationFrame(ensureCartOpen);
    window.setTimeout(ensureCartOpen, 350);
  });

  document.addEventListener('variant:added', () => {
    if (!isMobile()) return;
    window.setTimeout(ensureCartOpen, 650);
  });
})();

(() => {
  if (window.__swaggeroShopLookOpenFallback) return;
  window.__swaggeroShopLookOpenFallback = true;

  const closeModal = (root) => {
    const modal = root?.querySelector('[data-stl-modal]');
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('swaggero-shop-look-modal-open');
  };

  const openModal = (trigger) => {
    const root = trigger.closest('[data-swaggero-shop-look]');
    const modal = root?.querySelector('[data-stl-modal]');
    if (!root || !modal) return;

    const sourceLook = trigger.closest('[data-stl-mobile-look]');
    const requestedIndex = Number(trigger.dataset.lookIndex ?? sourceLook?.dataset.lookIndex ?? 0);
    const modalLooks = [...modal.querySelectorAll('[data-stl-modal-look]')];
    const resolvedIndex = Math.max(0, Math.min(requestedIndex, modalLooks.length - 1));

    modalLooks.forEach((look, index) => look.classList.toggle('is-active', index === resolvedIndex));
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('swaggero-shop-look-modal-open');
  };

  document.addEventListener('click', (event) => {
    const close = event.target.closest('[data-swaggero-shop-look] [data-stl-close]');
    if (close) {
      closeModal(close.closest('[data-swaggero-shop-look]'));
      return;
    }

    const trigger = event.target.closest(
      '[data-swaggero-shop-look] [data-stl-open], [data-swaggero-shop-look] [data-stl-mobile-hotspot]'
    );
    if (trigger) openModal(trigger);
  }, true);
})();

(() => {
  if (window.__swaggeroLinkedColorsToggle) return;
  window.__swaggeroLinkedColorsToggle = true;

  const mount = (root = document) => {
    root.querySelectorAll?.('.swaggero-linked-colors, .swaggero-featured-info__linked-colors').forEach((block) => {
      const list = block.querySelector('.swaggero-linked-colors__list, .swaggero-featured-info__linked-list');
      const toggle = block.querySelector('[data-linked-colors-toggle]');
      if (!list || !toggle || toggle.dataset.bound === 'true') return;

      const colors = list.querySelectorAll(':scope > a');
      toggle.hidden = colors.length <= 5;
      toggle.dataset.bound = 'true';
      toggle.setAttribute('aria-expanded', 'false');

      toggle.addEventListener('click', () => {
        const expanded = list.classList.toggle('is-expanded');
        toggle.textContent = expanded ? 'HIDE' : 'SHOW MORE';
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    });
  };

  mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1) mount(node);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

(() => {
  const delimit = (cents, precision, thousands, decimal) => {
    const value = (Number(cents || 0) / 100).toFixed(precision);
    const parts = value.split('.');
    const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return precision ? `${whole}${decimal}${parts[1]}` : whole;
  };

  const amountForPlaceholder = (cents, placeholder) => {
    switch (placeholder) {
      case 'amount_no_decimals': return delimit(cents, 0, ',', '.');
      case 'amount_with_space_separator': return delimit(cents, 2, ' ', '.');
      case 'amount_with_comma_separator': return delimit(cents, 2, '.', ',');
      case 'amount_with_apostrophe_separator': return delimit(cents, 2, "'", '.');
      case 'amount_no_decimals_with_comma_separator': return delimit(cents, 0, '.', ',');
      case 'amount_no_decimals_with_space_separator': return delimit(cents, 0, ' ', '.');
      case 'amount_no_decimals_with_apostrophe_separator': return delimit(cents, 0, "'", '.');
      default: return delimit(cents, 2, ',', '.');
    }
  };

  const repairMoney = (root = document) => {
    root.querySelectorAll?.('[data-swaggero-money-cents]').forEach((node) => {
      const text = node.textContent || '';
      if (!text.includes('{{')) return;
      node.textContent = text.replace(/\{\{\s*(amount(?:_[a-z_]+)?)\s*\}\}/g, (_, placeholder) => (
        amountForPlaceholder(node.dataset.swaggeroMoneyCents, placeholder)
      ));
    });
  };

  repairMoney();
  document.addEventListener('shopify:section:load', (event) => repairMoney(event.target));
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1) repairMoney(node);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
